/**
 * The HR permission catalogue — the one place a permission is written down. Everything else
 * (`Perm`, the login-token decoder, the Add/Edit User grid, the dependency checks, the Client
 * HR defaults) is derived from this in permission.service.ts. Audit of what each name gates:
 * docs/DEVELOPER_NOTES_HR_PERMISSIONS.md.
 *
 * Rules for an entry:
 *   - `name` is the label on the thing it unlocks, and must not claim more than it gates. It
 *     must survive the super-admin validator — letters and single interior spaces only, hence
 *     `Reports And Downloads`, `Open Or Close Drive`.
 *   - `code` is what the backend puts in `permissionToken`. Groups have none: the token lists
 *     actions only.
 *   - `requires` names the page its button lives on (see `Requirement`). An action inherits
 *     its group's, so set it only where it differs. Unconditional needs only.
 *   - `clientHrDefault` ticks it for a new Client HR. Mark a group only where its whole block
 *     is wanted: the group grants every action under it.
 *
 * Section and entry order is the grid's order.
 */

/**
 * Keys of what an entry is useless without. Each inner list is "at least one of", every list
 * must be met — `[['DRIVES_TAB', 'SR_TAB'], [...]]` reads "Drives Tab or SR Tab, and ...".
 */
export type Requirement = readonly (readonly string[])[];

export interface PermissionEntry {
  readonly key: string;
  readonly name: string;
  readonly code?: string;
  readonly requires?: Requirement;
  readonly clientHrDefault?: boolean;
}

/** A group covers a whole area: holding it grants every action listed under it. */
export interface PermissionGroupEntry extends PermissionEntry {
  readonly actions: readonly PermissionEntry[];
}

/** Names that answer only for themselves. */
export interface StandaloneBlock {
  readonly standalone: readonly PermissionEntry[];
}

export type PermissionBlock = PermissionGroupEntry | StandaloneBlock;

export interface PermissionSection {
  readonly label: string;
  readonly blocks: readonly PermissionBlock[];
}

// Where buttons live — drive list and candidate-details are on the Drives tab, view-candidate
// opens from the Drives or SR tab.
const ON_DRIVES_TAB: Requirement = [['DRIVES_TAB']];
const ON_DRIVES_OR_SR_TAB: Requirement = [['DRIVES_TAB', 'SR_TAB']];
const ON_CANDIDATES_TAB: Requirement = [['CANDIDATES_TAB']];
const ON_ANY_CANDIDATE_TAB: Requirement = [['DRIVES_TAB', 'CANDIDATES_TAB', 'SR_TAB']];
// only on view-candidate, the assessment report page
const ON_VIEW_CANDIDATE: Requirement = [...ON_DRIVES_OR_SR_TAB, ['VIEW_CANDIDATE_ASSESSMENT_REPORT']];

export const PERMISSION_CATALOGUE = [
  {
    label: 'HR Dashboard Tabs',
    blocks: [
      {
        // One per sidebar tab, no group — opens the tab only; actions inside keep their own names
        standalone: [
          { key: 'SR_TAB', name: 'SR Tab', code: 'a18380dc764b69b9' },
          { key: 'DRIVES_TAB', name: 'Drives Tab', code: '08ef87f2f5bc23cc', clientHrDefault: true },
          { key: 'CANDIDATES_TAB', name: 'Candidates Tab', code: 'f168ba50c0c7173b', clientHrDefault: true },
          { key: 'CONSOLIDATED_REPORT_TAB', name: 'Consolidated Report Tab', code: 'd4f85d0f5d02dd02' },
          { key: 'ASSESSMENT_TAB', name: 'Assessment Tab', code: '1fee6b13a633a391' },
          { key: 'STATS_TAB', name: 'Stats Tab', code: '802a3a5372043f17' },
        ],
      },
    ],
  },
  {
    label: 'Drives',
    blocks: [
      {
        key: 'DRIVE_MANAGEMENT', name: 'Drive Management', requires: ON_DRIVES_TAB, clientHrDefault: true,
        actions: [
          { key: 'SCHEDULE_DRIVE', name: 'Schedule Drive', code: '33808073dcbe1fc0', clientHrDefault: true },
          { key: 'SCHEDULE_BOT_DRIVE', name: 'Schedule Bot Drive', code: '6f93f45186bdc784', clientHrDefault: true },
          { key: 'EDIT_DRIVE', name: 'Edit Drive', code: '75cf54d0d5b452fd', clientHrDefault: true },
          { key: 'CLONE_DRIVE', name: 'Clone Drive', code: '96b60546e8b1847f', clientHrDefault: true },
          { key: 'OPEN_OR_CLOSE_DRIVE', name: 'Open Or Close Drive', code: 'cb75882da3dd0cbe', clientHrDefault: true },
          { key: 'DRIVE_SETTINGS', name: 'Drive Settings', code: '8e31899163526b2a', clientHrDefault: true },
          { key: 'IMPORT_SHORTLISTED', name: 'Import Shortlisted', code: 'eaca48257dfdf432', clientHrDefault: true },
        ],
      },
    ],
  },
  {
    label: 'Candidates',
    blocks: [
      {
        key: 'CANDIDATE_DATA', name: 'Candidate Data', requires: ON_ANY_CANDIDATE_TAB,
        actions: [
          // profile and resume
          { key: 'VIEW_CANDIDATE_PROFILE', name: 'View Candidate Profile', code: '24d2085e8baa65ff', clientHrDefault: true },
          // the view-candidate report page, Feeds included (was View Proctoring Feeds, same code)
          { key: 'VIEW_CANDIDATE_ASSESSMENT_REPORT', name: 'View Candidate Assessment Report', code: 'a708e02ed97f9c1d', requires: ON_DRIVES_OR_SR_TAB },
        ],
      },
      {
        key: 'CANDIDATE_ACTIONS', name: 'Candidate Actions', requires: ON_DRIVES_TAB,
        actions: [
          { key: 'SHORTLIST_CANDIDATES', name: 'Shortlist Candidates', code: '047870d09e66dde9', clientHrDefault: true },
          { key: 'BLACKLIST_CANDIDATE', name: 'Blacklist Candidate', code: '05987ac8ab103fbd', clientHrDefault: true },
          { key: 'DEREGISTER_FROM_DRIVE', name: 'Deregister From Drive', code: '0bdde7b16b121077', clientHrDefault: true },
          { key: 'RESET_LAST_ATTEMPT', name: 'Reset Last Attempt', code: '9e336134339dbabe', clientHrDefault: true },
          // Candidates tab row menu
          { key: 'RESET_CANDIDATE_PASSWORD', name: 'Reset Candidate Password', code: '025eb1ef35826496', requires: ON_CANDIDATES_TAB, clientHrDefault: true },
          { key: 'UNBLOCK_CANDIDATE', name: 'Unblock Candidate', code: '266455be12a977e1', requires: ON_CANDIDATES_TAB, clientHrDefault: true },
          { key: 'REEVALUATE_INTERVIEW_BOT', name: 'Reevaluate Interview Bot', code: '11fd8ca523b3d89c', clientHrDefault: true },
        ],
      },
      {
        standalone: [
          { key: 'BULK_SHORTLISTING', name: 'Bulk Shortlisting', code: '01ec39257aad4808', requires: ON_DRIVES_TAB, clientHrDefault: true },
          // The whole Invited Users (Workday) page — its header entry is on every HR screen
          { key: 'INVITED_USERS', name: 'Invited Users', code: 'c25ed96dd9b6081e' },
        ],
      },
    ],
  },
  {
    label: 'Reports & Downloads',
    blocks: [
      {
        key: 'REPORTS_AND_DOWNLOADS', name: 'Reports And Downloads', requires: ON_DRIVES_OR_SR_TAB,
        actions: [
          // Generate Reports also opens the Reports modal; Download Reports takes the file.
          { key: 'GENERATE_REPORTS', name: 'Generate Reports', code: '7eadf78e32db33a3', clientHrDefault: true },
          { key: 'DOWNLOAD_REPORTS', name: 'Download Reports', code: '27e007a79dc75a14', clientHrDefault: true },
          { key: 'DOWNLOAD_MERGED_URLS', name: 'Download Merged URLs', code: '045055a832dbc609', requires: ON_DRIVES_TAB },
          // IDE folder zip + Jupyter notebook only; everything else rides on Download Reports.
          { key: 'DOWNLOAD_CODE_RESPONSES', name: 'Download Code Responses', code: '1fdf21680924ad26', requires: ON_VIEW_CANDIDATE },
          { key: 'EXPORT_VIDEO_QUESTIONS', name: 'Export Video Questions', code: 'a89ae5ed504caeab', requires: ON_DRIVES_TAB },
          { key: 'GENERATE_CONSOLIDATED_REPORT', name: 'Generate Consolidated Report', code: '47cd147b7cc25f60', requires: ON_DRIVES_TAB, clientHrDefault: true },
          // candidate-details, interview-bot experience drives only
          { key: 'DOWNLOAD_JD_QUESTIONS', name: 'Download JD Questions', code: '811a4a77c0df2534', requires: ON_DRIVES_TAB },
        ],
      },
      {
        standalone: [
          { key: 'EVALUATION_REPORTS', name: 'Evaluation Reports', code: 'dee84921e4c72702', requires: ON_DRIVES_TAB, clientHrDefault: true },
          { key: 'PROCTORING_FEEDS_REPORT', name: 'Proctoring Feeds Report', code: '94ddfe20f6a108de', requires: ON_DRIVES_OR_SR_TAB },
          { key: 'MERGE_REPORTS', name: 'Merge Reports', code: '95946e53a1e19453', requires: ON_DRIVES_OR_SR_TAB },
          { key: 'AUDIO_OR_VIDEO_EVALUATION_REPORTS', name: 'Audio Or Video Evaluation Reports', code: 'f1986f7b5708e4dc', requires: ON_DRIVES_TAB },
        ],
      },
    ],
  },
  {
    label: 'Proctoring',
    blocks: [
      {
        // candidate-details and the schedule-drive form
        standalone: [
          { key: 'VIEW_PROCTORING_SETTINGS', name: 'View Proctoring Settings', code: '8eefded6c5d940f7', requires: ON_DRIVES_TAB, clientHrDefault: true },
          { key: 'EDIT_PROCTORING_SETTINGS', name: 'Edit Proctoring Settings', code: 'd6a707dad3a87f20', requires: [...ON_DRIVES_TAB, ['VIEW_PROCTORING_SETTINGS']], clientHrDefault: true },
        ],
      },
    ],
  },
  {
    label: 'Processing',
    blocks: [
      {
        standalone: [
          // its buttons sit inside the Evaluation Reports and Audio/Video Evaluation Report tabs
          { key: 'RETRY_EVALUATION_EVENTS', name: 'Retry Evaluation Events', code: '135534809242de56', requires: [['EVALUATION_REPORTS', 'AUDIO_OR_VIDEO_EVALUATION_REPORTS']], clientHrDefault: true },
          { key: 'RETRIGGER_PIPELINE', name: 'Retrigger Pipeline', code: '537736dfaf7762db', requires: ON_DRIVES_TAB, clientHrDefault: true },
        ],
      },
    ],
  },
  {
    label: 'Communication & Sharing',
    blocks: [
      {
        key: 'OUTBOUND_SHARING', name: 'Outbound Sharing', requires: ON_DRIVES_OR_SR_TAB,
        actions: [
          { key: 'SEND_MAIL_TO_CANDIDATES', name: 'Send Mail To Candidates', code: 'f97a2870c6d74725', clientHrDefault: true },
          // Share Drive modal — one per option; the Share Drive button shows for either.
          { key: 'GENERATE_SHARE_LINK', name: 'Generate Share Link', code: 'b05996b0cd29ff0b', requires: ON_DRIVES_TAB, clientHrDefault: true },
          { key: 'DOWNLOAD_APP_LINK', name: 'Download App Link', code: '916540051c86f66c', requires: ON_DRIVES_TAB },
          { key: 'GENERATE_PUBLIC_LINK', name: 'Generate Public Link', code: '96e951141e5b5224', requires: ON_VIEW_CANDIDATE, clientHrDefault: true },
        ],
      },
    ],
  },
] as const satisfies readonly PermissionSection[];
